// ui/chrome.ts — the fake verification modal shell (SPEC §2, §5, §7).
// Owns the persistent frame: header + suspicion ring, the level mount point (body),
// the decaying progress bar, the footer privacy disclosure, and the verdict beat
// overlay that masks judge latency (§10). Levels never touch this directly except
// through the mount host and the LevelContext.verdictBeat bridge.

import type { SuspicionMeter } from "@/engine/suspicion.ts";

const TOTAL_LEVELS = 15;

// Verdict beat timing (§10): 1.2s masking window; hold pending up to 8s; then reveal.
const CONSIDER_MS = 1200;
const MAX_PENDING_MS = 8000;
const REVEAL_HOLD_MS = 1700;

const FOOTER_DISCLOSURE =
  "sensor data is processed on your device and never uploaded · text answers are judged by AI";

export interface VerdictOptions {
  /** A judged level supplies the in-flight request; its resolution replaces `line`. */
  pending?: Promise<string>;
  /** Override the default hold before resolving (ms). */
  hold?: number;
}

export class Chrome {
  readonly stage: HTMLElement;
  private titleEl!: HTMLElement;
  private bodyEl!: HTMLElement;
  private footerEl!: HTMLElement;
  private ring!: HTMLElement;
  private progressFill!: HTMLElement;
  private verdictEl!: HTMLElement;
  private reducedMotion: boolean;

  constructor(stage: HTMLElement, reducedMotion: boolean) {
    this.stage = stage;
    this.reducedMotion = reducedMotion;
    this.build();
  }

  private build(): void {
    this.stage.innerHTML = "";

    const modal = el("div", "modal");
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-label", "Human verification");

    // Header: title + suspicion meter
    const header = el("div", "modal__header");
    this.titleEl = el("span", "modal__title");
    this.titleEl.textContent = "Verify you are human";

    const suspicion = el("div", "suspicion");
    this.ring = el("span", "suspicion__ring");
    this.ring.setAttribute("aria-hidden", "true");
    const suspicionLabel = el("span");
    suspicionLabel.textContent = "";
    suspicion.append(this.ring, suspicionLabel);
    header.append(this.titleEl, suspicion);

    // Body: the level mount point + the verdict overlay
    this.bodyEl = el("div", "modal__body");
    this.verdictEl = this.buildVerdict();
    this.bodyEl.append(this.verdictEl);

    // Progress bar (decays with the set)
    const progress = el("div", "progress");
    this.progressFill = el("div", "progress__fill");
    progress.append(this.progressFill);

    // Footer disclosure (§6)
    this.footerEl = el("div", "modal__footer");
    this.footerEl.textContent = FOOTER_DISCLOSURE;

    modal.append(header, this.bodyEl, progress, this.footerEl);
    this.stage.append(modal);
  }

  private buildVerdict(): HTMLElement {
    const v = el("div", "verdict");
    v.setAttribute("aria-live", "polite");
    const line = el("div", "verdict__line");
    const thinking = el("div", "verdict__thinking");
    thinking.append(el("span"), el("span"), el("span"));
    v.append(line, thinking);
    return v;
  }

  /** The host element a level mounts its own DOM into. Cleared between levels. */
  get host(): HTMLElement {
    // A wrapper distinct from the verdict overlay, so clearing level DOM never
    // removes the overlay.
    let host = this.bodyEl.querySelector<HTMLElement>(".level-host");
    if (!host) {
      host = el("div", "level-host");
      this.bodyEl.insertBefore(host, this.verdictEl);
    }
    return host;
  }

  clearHost(): void {
    this.host.replaceChildren();
  }

  setTitle(text: string): void {
    this.titleEl.textContent = text;
  }

  /** Advance the decaying progress bar. levelIndex is 0-based. */
  setProgress(levelIndex: number): void {
    const pct = Math.round(((levelIndex + 1) / TOTAL_LEVELS) * 100);
    this.progressFill.style.width = `${pct}%`;
  }

  /** Subscribe the suspicion ring to a meter. Returns an unsubscribe. */
  bindMeter(meter: SuspicionMeter): () => void {
    const offChange = meter.onChange((s) => {
      this.ring.dataset.strikes = String(s.strikes);
    });
    const offEvent = meter.onEvent(() => {
      this.ring.classList.remove("pulse");
      // reflow to restart the animation
      void this.ring.offsetWidth;
      this.ring.classList.add("pulse");
    });
    return () => {
      offChange();
      offEvent();
    };
  }

  /**
   * The diegetic verdict beat (§10). Shows the "considering" animation for the
   * masking window; if a judged request is in flight, hold (up to 8s) on the beat
   * until it resolves, then reveal that line; otherwise reveal `fallbackLine`.
   */
  async verdictBeat(fallbackLine: string, opts: VerdictOptions = {}): Promise<void> {
    const lineEl = this.verdictEl.querySelector<HTMLElement>(".verdict__line")!;
    const thinkingEl = this.verdictEl.querySelector<HTMLElement>(".verdict__thinking")!;

    lineEl.textContent = "";
    thinkingEl.style.display = "";
    this.verdictEl.classList.add("show");

    const consider = this.reducedMotion ? 0 : CONSIDER_MS;

    let line = fallbackLine;
    if (opts.pending) {
      // Race the masking window against the request. Whichever way, never exceed 8s.
      const guarded = withTimeout(opts.pending, MAX_PENDING_MS, fallbackLine);
      const [resolved] = await Promise.all([guarded, delay(consider)]);
      line = resolved || fallbackLine;
    } else {
      await delay(consider);
    }

    // Reveal
    thinkingEl.style.display = "none";
    lineEl.textContent = line;
    await delay(opts.hold ?? (this.reducedMotion ? 400 : REVEAL_HOLD_MS));

    this.verdictEl.classList.remove("show");
    // let the fade out finish before the level DOM changes underneath
    await delay(this.reducedMotion ? 0 : 240);
  }
}

// ── tiny DOM + async helpers ──────────────────────────────────────────

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

export function delay(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/** Resolve `p`, but if it takes longer than `ms`, resolve to `fallback` instead. */
export function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;
    const t = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(fallback);
      }
    }, ms);
    p.then(
      (v) => {
        if (!settled) {
          settled = true;
          window.clearTimeout(t);
          resolve(v);
        }
      },
      () => {
        if (!settled) {
          settled = true;
          window.clearTimeout(t);
          resolve(fallback);
        }
      },
    );
  });
}
